import type { ReactNode } from 'react';
import { Switch, Typography } from 'antd';
import {
  MessageOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  BellOutlined,
  ExclamationCircleFilled,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { NotificationPreferences } from '../../../api/providerMeApi';
import { SectionShell } from './SectionShell';

const { Text } = Typography;

interface NotificationsSectionProps {
  prefs: NotificationPreferences | null;
  onToggle: (key: keyof NotificationPreferences) => void;
}

interface Item {
  key: keyof NotificationPreferences;
  label: string;
  icon: ReactNode;
  color: string;
}

export function NotificationsSection({
  prefs,
  onToggle,
}: NotificationsSectionProps) {
  const { t } = useTranslation('provider');

  if (!prefs) {
    return (
      <SectionShell
        caption={t('profile.sections.notificationsCaption')}
        icon={<BellOutlined />}
        iconColor="#fa8c16"
      >
        <Text type="secondary" style={{ fontSize: 13 }}>
          {t('profile.notifications.loadFailed')}
        </Text>
      </SectionShell>
    );
  }

  const items: Item[] = [
    {
      key: 'enableTelegram',
      label: t('profile.notifications.telegramLabel'),
      icon: <MessageOutlined />,
      color: '#3390ec',
    },
    {
      key: 'enableReminders',
      label: t('profile.notifications.remindersLabel'),
      icon: <ClockCircleOutlined />,
      color: '#fa8c16',
    },
    {
      key: 'enableBookingUpdates',
      label: t('profile.notifications.bookingUpdatesLabel'),
      icon: <CheckCircleOutlined />,
      color: '#1f8b4c',
    },
  ];

  return (
    <SectionShell
      caption={t('profile.sections.notificationsCaption')}
      icon={<BellOutlined />}
      iconColor="#fa8c16"
    >
      {!prefs.enableTelegram && (
        <div
          role="alert"
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
            padding: '10px 12px',
            marginBottom: 12,
            background:
              'linear-gradient(135deg, rgba(51,144,236,0.06) 0%, rgba(51,144,236,0.14) 100%)',
            borderRadius: 10,
            border: '1px solid rgba(51,144,236,0.18)',
          }}
        >
          <ExclamationCircleFilled
            style={{
              color: 'var(--bl-accent, #3390ec)',
              fontSize: 14,
              marginTop: 1,
              flexShrink: 0,
            }}
          />
          <Text
            style={{
              fontSize: 12,
              lineHeight: 1.45,
              color: 'var(--bl-info-text, #1d6fd5)',
            }}
          >
            {t('profile.notifications.telegramDisabledHint')}
          </Text>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {items.map((item, idx) => {
          const isMaster = item.key === 'enableTelegram';
          const isDisabled = !isMaster && !prefs.enableTelegram;
          const checked = prefs[item.key];
          const activeColor = checked && !isDisabled ? item.color : '#9ca3af';

          return (
            <div
              key={item.key}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 0',
                borderBottom:
                  idx < items.length - 1
                    ? '1px solid var(--bl-divider, rgba(16,24,40,0.06))'
                    : 'none',
                opacity: isDisabled ? 0.55 : 1,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <div
                  aria-hidden
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 9,
                    background: `linear-gradient(135deg, ${activeColor}14 0%, ${activeColor}26 100%)`,
                    border: `1px solid ${activeColor}1a`,
                    color: activeColor,
                    fontSize: 14,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {item.icon}
                </div>
                <Text
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: 'var(--bl-text-strong, #0f172a)',
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {item.label}
                </Text>
              </div>
              <Switch
                disabled={isDisabled}
                checked={checked}
                onChange={() => onToggle(item.key)}
              />
            </div>
          );
        })}
      </div>
    </SectionShell>
  );
}
